import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, CalendarClock, Download, Forward, Plus, Scale, Search, SlidersHorizontal, Wallet } from 'lucide-react';
import * as hr from '../../api';
import { useHR } from '../../HRContext';
import Modal from '../../components/Modal';
import { DateInput, Select } from '../../components/Controls';
import { download, toCSV } from '../../format';
import { Chip } from '../attendance/shared';
import { Empty, InnerTabs, TypeChip, longDate, num, typeId, useFrozen } from './shared';

// Advanced Dashboard > Balance (Stephanet's leavemgmt/BalanceHub.jsx): Leave Balance per employee, year and leave type (with Adjust and CF),
// the Adjustments register and the Carry Forward register

const VIEWS = [['balance', 'Leave Balance', Wallet], ['adjust', 'Adjustments', SlidersHorizontal], ['cf', 'Carry Forward', Forward]];
const ADJ_TONE = { 'Manual Addition': 'green', 'Manual Deduction': 'red', Correction: 'blue', 'Opening Balance Correction': 'purple', 'Error Correction': 'orange', 'Carry Forward': 'teal' };
export const balanceStatus = b => (b.available <= 0 ? ['Exhausted', 'red'] : b.pending > 0 ? ['Pending Approval', 'yellow'] : b.available <= 3 ? ['Low Balance', 'orange'] : ['Available', 'green']);
export const yearsAround = y => [y - 2, y - 1, y, y + 1];

export default function BalanceHub({ data, reload, year, setYear, tick }) {
  const [view, setView] = useState('balance');
  const [adjusting, setAdjusting] = useState(null);
  const [carrying, setCarrying] = useState(null);
  const done = () => { setAdjusting(null); setCarrying(null); reload(); };
  return (
    <>
      <InnerTabs items={VIEWS} active={view} onChange={setView} sub>
        <span className="grow" />
        <label className="lvm-yearpick">Year <Select value={year} onChange={e => setYear(Number(e.target.value))}>{yearsAround(data.year).map(y => <option key={y} value={y}>{y}</option>)}</Select></label>
      </InnerTabs>
      {view === 'balance' && <BalanceTable data={data} year={year} tick={tick} onAdjust={setAdjusting} onCarry={setCarrying} />}
      {view === 'adjust' && <AdjustRegister data={data} year={year} tick={tick} onNew={() => setAdjusting({})} />}
      {view === 'cf' && <CarryRegister data={data} year={year} tick={tick} onNew={() => setCarrying({})} />}
      {adjusting && <AdjustModal data={data} year={year} emp={adjusting.emp} leaveType={adjusting.type} onClose={() => setAdjusting(null)} onDone={done} />}
      {carrying && <CarryModal data={data} toYear={year} emp={carrying.emp} leaveType={carrying.type} onClose={() => setCarrying(null)} onDone={done} />}
    </>
  );
}

// ---------------------------------------------------------------- Leave Balance
function BalanceTable({ data, year, tick, onAdjust, onCarry }) {
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [dept, setDept] = useState('');
  const [status, setStatus] = useState('');
  const [active, setActive] = useState(true);
  const all = useMemo(() => data.employees.flatMap(e => Object.entries(e.balances).map(([t, b]) => ({ e, t, b, st: balanceStatus(b) }))), [data]);
  const rows = all.filter(({ e, t, b, st }) => (!type || t === type) && (!dept || e.department === dept) && (!status || st[0] === status)
    && (!active || b.used || b.pending || b.carried || b.adjusted || b.monetized || b.expired)
    && (!q || [e.name, e.employee_no].some(v => String(v || '').toLowerCase().includes(q.toLowerCase()))));
  const ref = useFrozen(2, [tick, rows.length]);
  const types = [...new Set(all.map(x => x.t))];
  const exportCsv = () => download(`leave-balance-${year}.csv`, toCSV([
    { label: 'Employee', get: x => x.e.name }, { label: 'Employee ID', get: x => x.e.employee_no }, { label: 'Year', get: () => year }, { label: 'Leave Type', get: x => x.t },
    { label: 'Entitlement', get: x => x.b.entitled }, { label: 'Carried Fwd', get: x => x.b.carried }, { label: 'Adjusted', get: x => x.b.adjusted },
    { label: 'Used', get: x => x.b.used + x.b.monetized }, { label: 'Pending', get: x => x.b.pending }, { label: 'Expired', get: x => x.b.expired || 0 },
    { label: 'Remaining', get: x => x.b.available }, { label: 'Status', get: x => x.st[0] },
  ], rows));
  return (
    <div className="mdr-card mdr-table-card">
      <div className="mds-table-head">
        <b>Leave Balance <span className="mdl-year">{year} · {rows.length} row(s)</span></b>
        <div className="att2-filters no-print">
          <div className="mdr-search mds-quick"><Search size={14} /><input placeholder="Search employee..." value={q} onChange={e => setQ(e.target.value)} /></div>
          <Select value={type} onChange={e => setType(e.target.value)}><option value="">All Types</option>{types.map(t => <option key={t} value={t}>{t}</option>)}</Select>
          <Select value={dept} onChange={e => setDept(e.target.value)}><option value="">All Departments</option>{data.departments.map(d => <option key={d} value={d}>{d}</option>)}</Select>
          <Select value={status} onChange={e => setStatus(e.target.value)}><option value="">All Status</option>{['Available', 'Pending Approval', 'Low Balance', 'Exhausted'].map(s => <option key={s} value={s}>{s}</option>)}</Select>
          <label className="lvm-check"><input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} /> With activity only</label>
          {data.can.export && <button className="mdr-btn" onClick={exportCsv} disabled={!rows.length}><Download size={13} /> Export</button>}
        </div>
      </div>
      <div className="mdr-scroll">
        <table className="mdr-table mds-table lvm-frozen lvm-f2" ref={ref}>
          <thead><tr>{data.can.edit && <th className="c no-print">Actions</th>}<th>Employee</th><th className="c">Year</th><th>Leave Type</th><th className="c">Entitlement</th>
            <th className="c">Carried Fwd</th><th className="c">Adjusted</th><th className="c">Used</th><th className="c">Pending</th><th className="c">Expired</th>
            <th className="c">Remaining</th><th>Status</th></tr></thead>
          <tbody key={tick}>
            {rows.map(({ e, t, b, st }, i) => (
              <tr key={`${e.id}${t}`} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 12}ms` }}>
                {data.can.edit && <td className="c no-print nowrap">
                  <button className="mds-view" onClick={() => onAdjust({ emp: e, type: t })}><Scale size={12} /> Adjust</button>
                  <button className="lvm-cf" title="Carry forward unused days from the previous year" onClick={() => onCarry({ emp: e, type: t })}>CF</button>
                </td>}
                <td className="lvm-emp"><b>{e.name}</b><small>{e.employee_no}</small></td>
                <td className="c">{year}</td><td><TypeChip t={t} /></td>
                <td className="c">{num(b.entitled)}</td><td className="c">{num(b.carried)}</td>
                <td className={`c ${b.adjusted < 0 ? 'rd' : b.adjusted > 0 ? 'g' : ''}`}>{b.adjusted > 0 ? '+' : ''}{num(b.adjusted)}</td>
                <td className="c am">{num(b.used + b.monetized)}</td><td className="c in">{num(b.pending)}</td><td className="c rd">{num(b.expired || 0)}</td>
                <td className={`c b ${b.available <= 0 ? 'rd' : 'g'}`}>{num(b.available)}</td>
                <td><Chip tone={st[1]}>{st[0]}</Chip></td>
              </tr>
            ))}
            {!rows.length && <Empty cols={12}>No balances match{active ? ' — untick "With activity only" to see every employee' : ''}.</Empty>}
          </tbody>
        </table>
      </div>
      <div className="mdr-foot att2-foot"><span>Remaining = Entitlement + Carried Fwd + Adjusted − Used (incl. monetized) − Pending − Expired · carried days are used first and expire on their expiry date</span></div>
    </div>
  );
}

// ---------------------------------------------------------------- Adjustments register
function AdjustRegister({ data, year, tick, onNew }) {
  const rows = data.adjustments.filter(a => a.kind === 'adjustment' && a.leave_year === year);
  const exportCsv = () => download(`leave-adjustments-${year}.csv`, toCSV([
    { label: 'Employee', get: a => a.name }, { label: 'Year', get: a => a.leave_year }, { label: 'Leave Type', get: a => a.leave_type },
    { label: 'Adjustment Type', get: a => a.adjustment_type }, { label: 'Days', get: a => a.days }, { label: 'Balance Before', get: a => a.balance_before ?? '' },
    { label: 'Balance After', get: a => a.balance_after ?? '' }, { label: 'Reason', get: a => a.reason }, { label: 'Approved By', get: a => a.approved_by }, { label: 'Date', get: a => (a.created || '').slice(0, 10) },
  ], rows));
  return (
    <div className="mdr-card mdr-table-card">
      <div className="mds-table-head">
        <b>Leave Adjustments <span className="mdl-year">{year}</span></b>
        <div className="att2-filters no-print">
          {data.can.export && <button className="mdr-btn" onClick={exportCsv} disabled={!rows.length}><Download size={13} /> Export</button>}
          {data.can.edit && <button className="mds-run sm" onClick={onNew}><Plus size={13} /> New Adjustment</button>}
        </div>
      </div>
      <div className="mdr-scroll">
        <table className="mdr-table mds-table">
          <thead><tr><th>Employee</th><th className="c">Year</th><th>Leave Type</th><th>Adjustment Type</th><th className="c">Days</th><th className="c">Balance Before</th>
            <th className="c">Balance After</th><th>Reason</th><th>Approved By</th><th>Date</th></tr></thead>
          <tbody key={tick}>
            {rows.map((a, i) => (
              <tr key={a.id} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }}>
                <td className="lvm-emp"><b>{a.name}</b><small>{a.employee_no}</small></td><td className="c">{a.leave_year}</td><td><TypeChip t={a.leave_type} /></td>
                <td><Chip tone={ADJ_TONE[a.adjustment_type]}>{a.adjustment_type}</Chip></td>
                <td className={`c b ${a.days < 0 ? 'rd' : 'g'}`}>{a.days > 0 ? '+' : ''}{num(a.days)}</td>
                <td className="c">{num(a.balance_before)}</td><td className="c b">{num(a.balance_after)}</td>
                <td className="lvm-reason">{a.reason}</td><td className="nowrap">{a.approved_by || '—'}</td><td className="nowrap">{(a.created || '').slice(0, 10)}</td>
              </tr>
            ))}
            {!rows.length && <Empty cols={10}>No adjustments in {year}.</Empty>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Carry Forward register
function CarryRegister({ data, year, tick, onNew }) {
  const rows = data.adjustments.filter(a => a.kind === 'carry_forward' && (a.leave_year === year || a.from_year === year));
  const exportCsv = () => download(`leave-carry-forward-${year}.csv`, toCSV([
    { label: 'Employee', get: a => a.name }, { label: 'From Year', get: a => a.from_year }, { label: 'Into Year', get: a => a.leave_year }, { label: 'Leave Type', get: a => a.leave_type },
    { label: 'Unused Days', get: a => a.unused_days ?? '' }, { label: 'Max Allowed', get: a => a.max_allowed ?? '' }, { label: 'Carried Fwd Days', get: a => a.days },
    { label: 'Expiry Date', get: a => a.expiry_date || '' }, { label: 'Status', get: a => a.cf_status }, { label: 'Approved By', get: a => a.approved_by },
  ], rows));
  return (
    <div className="mdr-card mdr-table-card">
      <div className="mds-table-head">
        <b>Carry Forward <span className="mdl-year">into or out of {year}</span></b>
        <div className="att2-filters no-print">
          {data.can.export && <button className="mdr-btn" onClick={exportCsv} disabled={!rows.length}><Download size={13} /> Export</button>}
          {data.can.edit && <button className="mds-run sm" onClick={onNew}><Plus size={13} /> New Carry Forward</button>}
        </div>
      </div>
      <div className="mdr-scroll">
        <table className="mdr-table mds-table">
          <thead><tr><th>Employee</th><th className="c">Year</th><th>Leave Type</th><th className="c">Unused Days</th><th className="c">Max Allowed</th><th className="c">Carried Fwd Days</th>
            <th>Expiry Date</th><th>Status</th><th>Approved By</th></tr></thead>
          <tbody key={tick}>
            {rows.map((a, i) => (
              <tr key={a.id} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }}>
                <td className="lvm-emp"><b>{a.name}</b><small>{a.employee_no}</small></td>
                <td className="c nowrap">{a.from_year ?? '—'} <ArrowRightLeft size={11} /> {a.leave_year}</td><td><TypeChip t={a.leave_type} /></td>
                <td className="c">{num(a.unused_days)}</td><td className="c">{num(a.max_allowed)}</td><td className="c b g">{num(a.days)}</td>
                <td className="nowrap">{a.expiry_date ? longDate(a.expiry_date) : 'No expiry'}</td>
                <td><Chip tone={a.cf_status === 'Expired' ? 'gray' : 'green'}>{a.cf_status}</Chip></td><td className="nowrap">{a.approved_by || '—'}</td>
              </tr>
            ))}
            {!rows.length && <Empty cols={9}>No carry forward into or out of {year}.</Empty>}
          </tbody>
        </table>
      </div>
      <div className="mdr-foot att2-foot"><span>Carried days = the lower of the unused days and the leave type's Max Carry Forward · unused carried days expire on the expiry date</span></div>
    </div>
  );
}

// ---------------------------------------------------------------- Adjust (also used by the Leave Balances tab)
export function AdjustModal({ data, year, emp: fixedEmp, leaveType, onClose, onDone }) {
  const { notify } = useHR();
  const [empId, setEmpId] = useState(fixedEmp ? String(fixedEmp.id) : '');
  const emp = fixedEmp || data.employees.find(e => String(e.id) === empId);
  const tracked = data.types.filter(t => t.has_balance && t.active !== false && (!emp || emp.balances[t.name]));
  const [f, setF] = useState({ leave_type: leaveType || 'Annual', adjustment_type: '', days: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const b = emp?.balances[f.leave_type];
  const raw = Number(f.days) || 0;
  const d = f.adjustment_type === 'Manual Deduction' ? -Math.abs(raw) : f.adjustment_type === 'Manual Addition' ? Math.abs(raw) : raw;
  const save = e => {
    e.preventDefault();
    setBusy(true);
    hr.createAdjustment({ employee_id: emp.id, leave_type_id: typeId(data, f.leave_type), leave_year: year, adjustment_type: f.adjustment_type, days: d, reason: f.reason })
      .then(r => { notify(`${emp.name}: ${f.leave_type} ${num(r.balance_before)} → ${num(r.balance_after)} day(s)`); onDone(); })
      .catch(err => notify(err.message, 'bad')).finally(() => setBusy(false));
  };
  return (
    <Modal className="mdl-modal" title={<><Scale size={18} /> Leave Adjustment{emp ? ` — ${emp.name}` : ''}</>} onClose={() => !busy && onClose()}>
      <form className="mdl-modal-body" onSubmit={save}>
        <div className="lvm-grid two">
          {!fixedEmp && <label className="mdl-field">Employee *<Select value={empId} onChange={e => setEmpId(e.target.value)} required>
            <option value="">Select employee...</option>{data.employees.map(x => <option key={x.id} value={x.id}>{`${x.name} (${x.employee_no})`}</option>)}</Select></label>}
          <label className="mdl-field">Leave Type<Select value={f.leave_type} onChange={e => set('leave_type', e.target.value)}>{tracked.map(x => <option key={x.id} value={x.name}>{x.name}</option>)}</Select></label>
          <label className="mdl-field">Adjustment Type *<Select value={f.adjustment_type} onChange={e => set('adjustment_type', e.target.value)} required>
            <option value="">Select...</option>{data.adjustment_types.map(x => <option key={x} value={x}>{x}</option>)}</Select></label>
          <label className="mdl-field">Days *<input type="number" step="0.5" value={f.days} onChange={e => set('days', e.target.value)} required
            placeholder={['Manual Addition', 'Manual Deduction'].includes(f.adjustment_type) ? 'Number of days' : '+ adds, − removes'} /></label>
          <label className="mdl-field">Leave Year<input value={year} readOnly /></label>
        </div>
        {b && <div className="lvm-calc">
          <div><small>Balance Before</small><b>{num(b.remaining)}</b><span>{num(b.available)} available</span></div>
          <div className={d < 0 ? 'bad' : d > 0 ? 'ok' : ''}><small>Change</small><b>{d > 0 ? '+' : ''}{num(d)}</b><span>day(s)</span></div>
          <div className={b.remaining + d < 0 ? 'bad' : ''}><small>Balance After</small><b>{num(b.remaining + d)}</b><span>days</span></div>
        </div>}
        <label className="mdl-field">Reason *<textarea rows={2} value={f.reason} onChange={e => set('reason', e.target.value)} required placeholder="Why the balance changes…" /></label>
        <div className="mdh-form-foot">
          <button type="button" className="mdr-btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="mds-run" disabled={busy || !emp || !f.adjustment_type || !raw || !f.reason.trim()}><Scale size={14} /> {busy ? 'Saving…' : 'Save Adjustment'}</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- Carry forward
function CarryModal({ data, toYear, emp: fixedEmp, leaveType, onClose, onDone }) {
  const { notify } = useHR();
  const [empId, setEmpId] = useState(fixedEmp ? String(fixedEmp.id) : '');
  const emp = fixedEmp || data.employees.find(e => String(e.id) === empId);
  const cfTypes = data.types.filter(t => t.has_balance && t.active !== false && t.max_carry_forward > 0);
  const [type, setType] = useState(cfTypes.some(t => t.name === leaveType) ? leaveType : cfTypes[0]?.name || '');
  const [fromYear, setFromYear] = useState(toYear - 1);
  const [fig, setFig] = useState(null);
  const [days, setDays] = useState('');
  const [expiry, setExpiry] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setFig(null);
    if (!emp || !type) return;
    hr.previewCarryForward({ employee_id: emp.id, leave_type_id: typeId(data, type), from_year: fromYear })
      .then(r => { setFig(r); setDays(String(r.can_carry)); setExpiry(r.default_expiry); }).catch(e => notify(e.message, 'bad'));
  }, [emp?.id, type, fromYear]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = e => {
    e.preventDefault();
    setBusy(true);
    hr.carryForward({ employee_id: emp.id, leave_type_id: typeId(data, type), from_year: fromYear, days: Number(days), expiry_date: expiry, reason: reason || null })
      .then(r => { notify(`${emp.name}: ${num(r.days)} day(s) of ${type} leave carried ${fromYear} → ${fromYear + 1}`); onDone(); })
      .catch(err => notify(err.message, 'bad')).finally(() => setBusy(false));
  };
  const ok = fig && Number(days) > 0 && Number(days) <= fig.can_carry;
  return (
    <Modal className="mdl-modal" title={<><Forward size={18} /> Carry Forward{emp ? ` — ${emp.name}` : ''}</>} onClose={() => !busy && onClose()}>
      <form className="mdl-modal-body" onSubmit={save}>
        {!cfTypes.length && <div className="mdl-warn">No leave type allows carry forward. Set Max Carry Forward on a leave type in Leave Types first.</div>}
        <div className="lvm-grid two">
          {!fixedEmp && <label className="mdl-field">Employee *<Select value={empId} onChange={e => setEmpId(e.target.value)} required>
            <option value="">Select employee...</option>{data.employees.map(x => <option key={x.id} value={x.id}>{`${x.name} (${x.employee_no})`}</option>)}</Select></label>}
          <label className="mdl-field">Leave Type<Select value={type} onChange={e => setType(e.target.value)}>{cfTypes.map(x => <option key={x.id} value={x.name}>{x.name}</option>)}</Select></label>
          <label className="mdl-field">Carry From Year<Select value={fromYear} onChange={e => setFromYear(Number(e.target.value))}>
            {[toYear - 3, toYear - 2, toYear - 1, toYear].map(y => <option key={y} value={y}>{`${y} → ${y + 1}`}</option>)}</Select></label>
          <label className="mdl-field">Days to Carry *<input type="number" step="0.5" min="0.5" max={fig?.can_carry} value={days} onChange={e => setDays(e.target.value)} required /></label>
          <label className="mdl-field">Expiry Date *<DateInput value={expiry} onChange={e => setExpiry(e.target.value)} required /></label>
        </div>
        {fig && <div className="lvm-calc">
          <div><small>Unused {fig.from_year}</small><b>{num(fig.unused)}</b><span>remaining at year end</span></div>
          <div><small>Max Allowed</small><b>{num(fig.max_allowed)}</b><span>leave type limit</span></div>
          <div><small>Already Carried</small><b>{num(fig.already_carried)}</b><span>into {fig.to_year}</span></div>
          <div className={fig.can_carry > 0 ? 'ok' : 'bad'}><small>Can Carry</small><b>{num(fig.can_carry)}</b><span>into {fig.to_year}</span></div>
        </div>}
        <p className="lvm-hint"><CalendarClock size={13} /> Carried days are used before the new year's entitlement; any still unused on the expiry date lapse.</p>
        <label className="mdl-field">Note<textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder={`Unused ${type || ''} leave carried from ${fromYear}`} /></label>
        <div className="mdh-form-foot">
          <button type="button" className="mdr-btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="mds-run" disabled={busy || !ok}><Forward size={14} /> {busy ? 'Saving…' : 'Carry Forward'}</button>
        </div>
      </form>
    </Modal>
  );
}
